/**
 * Pure time-range and bucketing helpers shared by Today and Trends.
 * Kept free of DOM/React so they are trivially unit-testable.
 */

import type { MetricBucket } from '../api.js';

export type RangeKey = '7d' | '30d' | '90d' | '1y';

const DAY_MS = 86_400_000;

export const RANGE_DAYS: Record<RangeKey, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '1y': 365,
};

/** ISO bounds for a preset range ending at `now` (default: current time). */
export function rangeBounds(key: RangeKey, now: Date = new Date()): { from: string; to: string } {
  const to = now;
  const from = new Date(to.getTime() - RANGE_DAYS[key] * DAY_MS);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * Bucket auto-selection: raw for a week or less, hourly up to a quarter, else
 * daily. Driven by the span in days so custom ranges pick sensibly too.
 */
export function autoBucket(spanDays: number): MetricBucket {
  if (spanDays <= 7) return 'raw';
  if (spanDays <= 90) return 'hour';
  return 'day';
}

/** Span in whole days between two ISO timestamps (rounded up, min 1). */
export function spanDays(fromIso: string, toIso: string): number {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  return Math.max(1, Math.ceil(ms / DAY_MS));
}

/** Local calendar-day bounds (midnight→now) as ISO — for "today" sums. */
export function todayBounds(now: Date = new Date()): { from: string; to: string } {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return { from: start.toISOString(), to: now.toISOString() };
}

/** Bounds for the last `n` days ending now (for tile sparklines). */
export function lastNDays(n: number, now: Date = new Date()): { from: string; to: string } {
  const from = new Date(now.getTime() - n * DAY_MS);
  return { from: from.toISOString(), to: now.toISOString() };
}

/**
 * Bounds for the `n` days ending at `end` (inclusive) as ISO — used to jump a
 * view to a past window (e.g. the Sleep tab's "show latest data" shortcut, which
 * anchors 90 days on the newest recorded session).
 */
export function nDaysEnding(n: number, end: Date): { from: string; to: string } {
  const from = new Date(end.getTime() - n * DAY_MS);
  return { from: from.toISOString(), to: end.toISOString() };
}
